import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useExternal } from 'ahooks';
import { uuid } from '@/utils';

declare global {
    interface Window {
        luckysheet: any;
    }
}

interface IProps {
    sheetRef?: React.MutableRefObject<any>;
    excelData: Array<any>;
    style?: React.CSSProperties;
    cellUpdated?: Function;
    allowEdit?: boolean;
}

const beforeFocus = (selector: any) => {
    const el = document.querySelector(selector);
    const focusFnc = el.focus;
    el.focus = function () {
        return focusFnc.call(this, {
            preventScroll: true,
        });
    };
};

const Luckysheet: React.FC<IProps> = props => {
    const { sheetRef, excelData, style = {}, cellUpdated, allowEdit = true } = props;
    const ch_width = excelData?.[0]?.ch_width || null;
    const ch_height = excelData?.[0]?.ch_height || null;

    const pluginStatus = useExternal('https://gd-static-file.oss-cn-shanghai.aliyuncs.com/luckysheet/dist/plugins/js/plugin.js', {
        js: {
            async: true,
        },
    });
    const umdStatus = useExternal('https://gd-static-file.oss-cn-shanghai.aliyuncs.com/luckysheet/dist/luckysheet.umd.js', {
        js: {
            async: true,
        },
    });
    const pluginsCssStatus = useExternal('https://gd-static-file.oss-cn-shanghai.aliyuncs.com/luckysheet/dist/plugins/css/pluginsCss.css');
    const pluginsStatus = useExternal('https://gd-static-file.oss-cn-shanghai.aliyuncs.com/luckysheet/dist/plugins/plugins.css');
    const luckysheetCssStatus = useExternal('https://gd-static-file.oss-cn-shanghai.aliyuncs.com/luckysheet/dist/css/luckysheet.css');
    const iconfontStatus = useExternal('https://gd-static-file.oss-cn-shanghai.aliyuncs.com/luckysheet/dist/assets/iconfont/iconfont.css');

    const loadResourceSuccess = useMemo(() => {
        return (
            pluginStatus === 'ready' &&
            umdStatus === 'ready' &&
            pluginsCssStatus === 'ready' &&
            pluginsStatus === 'ready' &&
            luckysheetCssStatus === 'ready' &&
            iconfontStatus === 'ready'
        );
    }, [pluginStatus, umdStatus, pluginsCssStatus, pluginsStatus, luckysheetCssStatus, iconfontStatus]);

    const containerId = useRef(`luckysheet-${uuid()}`);

    /** 利用反射把值传的到处都是，并且总是新的 */
    const responseSheet = useMemo(() => {
        if (!loadResourceSuccess) return;

        const response: any = { ...(window.luckysheet || {}) };
        Object.keys(window.luckysheet || {}).forEach(key => {
            Object.defineProperty(response, key, {
                get: () => {
                    return window.luckysheet[key as keyof typeof window.luckysheet];
                },
            });
        });
        return response;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadResourceSuccess]);

    useImperativeHandle(sheetRef, () => responseSheet, [loadResourceSuccess]);

    // const [width, setWidth] = useState('100%');
    // const [height, setHeight] = useState('100%');

    const onRichTextEditorBlur = (e: any) => {
        console.log(e?.relatedTarget?.className.indexOf('luckysheet'));
        if (e?.relatedTarget?.className.indexOf('luckysheet') > -1) {
            return;
        }
        window.luckysheet?.exitEditMode();
    };

    const initLuckysheet = () => {
        window.luckysheet?.destroy();

        const defaultOptions = {
            title: '', // 设定表格名称
            lang: 'zh', // 设定表格语言
            allowCopy: false, // 允许复制
            showtoolbar: false, // 是否显示工具栏
            // 仅显示工作表保护、边框、合并单元格
            showtoolbarConfig: {
                undoRedo: false, // 撤销重做，注意撤消重做是两个按钮，由这一个配置决定显示还是隐藏
                paintFormat: false, // 格式刷
                currencyFormat: false, // 货币格式
                percentageFormat: false, // 百分比格式
                numberDecrease: false, // '减少小数位数'
                numberIncrease: false, // '增加小数位数
                moreFormats: false, // '更多格式'
                font: false, // '字体'
                fontSize: false, // '字号大小'
                bold: false, // '粗体 (Ctrl+B)'
                italic: false, // '斜体 (Ctrl+I)'
                strikethrough: false, // '删除线 (Alt+Shift+5)'
                underline: false, // '下划线 (Alt+Shift+6)'
                textColor: false, // '文本颜色'
                fillColor: false, // '单元格颜色'
                border: false, // '边框'
                mergeCell: false, // '合并单元格'
                horizontalAlignMode: false, // '水平对齐方式'
                verticalAlignMode: false, // '垂直对齐方式'
                textWrapMode: false, // '换行方式'
                textRotateMode: false, // '文本旋转方式'
                image: false, // '插入图片'
                link: false, // '插入链接'
                chart: false, // '图表'（图标隐藏，但是如果配置了chart插件，右击仍然可以新建图表）
                postil: false, // '批注'
                pivotTable: false, // '数据透视表'
                function: false, // '公式'
                frozenMode: false, // '冻结方式'
                sortAndFilter: false, // '排序和筛选'
                conditionalFormat: false, // '条件格式'
                dataVerification: false, // '数据验证'
                splitColumn: false, // '分列'
                screenshot: false, // '截图'
                findAndReplace: false, // '查找替换'
                protection: false, // '工作表保护'
                print: false, // '打印'
            },
            showinfobar: false, // 是否显示顶部信息栏
            showsheetbar: false, // 是否显示底部sheet页按钮
            showstatisticBar: false, // 是否显示底部计数栏
            showstatisticBarConfig: {
                count: false, // 计数栏
                view: false, // 打印视图
                zoom: false, // 缩放
            },
            enableAddRow: false, // 允许添加行
            enableAddBackTop: false, // 允许回到顶部
            cellRightClickConfig: {
                copy: false, // 复制
                copyAs: false, // 复制为
                paste: false, // 粘贴
                insertRow: false, // 插入行
                insertColumn: false, // 插入列
                deleteRow: false, // 删除选中行
                deleteColumn: false, // 删除选中列
                deleteCell: false, // 删除单元格
                hideRow: false, // 隐藏选中行和显示选中行
                hideColumn: false, // 隐藏选中列和显示选中列
                rowHeight: false, // 行高
                columnWidth: false, // 列宽
                clear: false, // 清除内容
                matrix: false, // 矩阵操作选区
                sort: false, // 排序选区
                filter: false, // 筛选选区
                chart: false, // 图表生成
                image: false, // 插入图片
                link: false, // 插入链接
                data: false, // 数据验证
                cellFormat: false, // 设置单元格格式
            },
            sheetFormulaBar: false, // 是否显示公式栏
            data: [
                {
                    name: 'Sheet1',
                    color: '',
                    index: 1,
                    status: 0,
                    order: 1,
                    celldata: [],
                    config: {},
                    row: 36, // 行数
                    column: 18, // 列数
                },
            ],
        };

        const mergeOptions = {
            ...defaultOptions,
            container: containerId.current, // luckysheet为容器id
            data: excelData?.length ? excelData : defaultOptions.data,
            allowEdit: allowEdit, // 是否允许前台进行表格编辑
            editMode: !allowEdit, // 是否为非编辑模式 与allowEdit功能基本相同，含义相反
            hook: {
                cellUpdated: function () {
                    cellUpdated?.();
                },
                workbookCreateAfter: function () {
                    window.luckysheet?.setRangeShow(window.luckysheet?.getRange(), { show: false });
                },
                cellMousedownBefore() {
                    beforeFocus('#luckysheet-rich-text-editor');
                },
            },
        };
        window.luckysheet?.create(mergeOptions);

        console.log(mergeOptions);

        setTimeout(() => {
            document.getElementById('luckysheet-rich-text-editor')?.addEventListener('blur', onRichTextEditorBlur);
        }, 100);
    };

    useEffect(() => {
        if (loadResourceSuccess) {
            // 初始化给一个默认的 form
            setTimeout(() => {
                initLuckysheet();
            }, 100);
        }
    }, [loadResourceSuccess]);

    useEffect(() => {
        if (excelData) {
            setTimeout(() => {
                window.luckysheet?.refresh();
            }, 0);
        }
    }, [excelData]);

    useEffect(() => {
        return () => {
            window.luckysheet?.destroy();
            document.getElementById('luckysheet-rich-text-editor')?.removeEventListener('blur', onRichTextEditorBlur);
        };
    }, []);

    return (
        <div
            id={containerId.current}
            style={{
                position: 'relative',
                marginTop: '20px',
                padding: 0,
                overflow: 'auto',
                width: ch_width && ch_width < 1136 ? `${ch_width - 50}px` : '1136px',
                height: ch_height && ch_height < 400 ? `${ch_height - 30}px` : '400px',
                maxWidth: '100%',
                ...style,
            }}
        ></div>
    );
};

export default Luckysheet;
